import React from "react";
import { Button, Input } from "@wac-ui-dashboard/wac_component_library";
import Select from "react-select";
import useAddCorporateOffer from "./useAddCorporateOffer";

const OFFER_TYPES = [
  { id: "prepaid", name: "Prepaid — the organisation pays" },
  { id: "discounted", name: "Discounted — the employee pays" },
];

const CODE_TYPES = [
  { id: "shared", name: "Shared — one code for everyone" },
  { id: "unique", name: "Unique — one single-use code per seat" },
];

const ELIGIBILITY_TYPES = [
  { id: "open", name: "Open — anyone with the code" },
  { id: "mobile_list", name: "Mobile list" },
  { id: "email_domain", name: "Email domain" },
];

const money = (value) =>
  value === undefined || value === null
    ? "-"
    : `KD ${Number(value).toFixed(3)}`;

const AddCorporateOffer = ({ isStickyFooter, handleCreateClick, refetch }) => {
  const {
    formik,
    plans,
    isEdit,
    planObj,
    pricing,
    isLocked,
    usedSeats,
    statusLabel,
    isOfferSaving,
    isPricingLoading,
    handleMobileTextChange,
    handleMobileCsvChange,
    handleSampleCsvDownload,
    getFieldError,
    closeModal,
  } = useAddCorporateOffer(handleCreateClick, refetch);

  return (
    <div className="row">
      <div className="input-wrap pro-mb-4">
        {isLocked && (
          <div className="pro-p-3 pro-mb-4 pro-bg-light rounded-3">
            <p className="pro-font-sm pro-mb-0">
              This offer is {String(statusLabel ?? "").replace("_", " ")}. The
              organisation was quoted on its terms, so only the seat count and
              the end date can still be changed.
            </p>
          </div>
        )}

        <Input
          label={"Organisation"}
          type="text"
          id="organisation"
          name="organisation"
          className={`pro-input lg ${getFieldError("organisation") && " error"}`}
          {...formik.getFieldProps("organisation")}
          error={getFieldError("organisation")}
          errorMessage={getFieldError("organisation")}
          disabled={isLocked}
        />

        <div className="input-wrap pro-mb-4">
          <label htmlFor="plan_id" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Plan
          </label>
          <Select
            id="plan_id"
            name="plan_id"
            placeholder={"Select a published plan"}
            className={`pro-input lg ${getFieldError("plan_id") && " error"}`}
            classNamePrefix="pro-input"
            options={plans ?? []}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.id}
            value={planObj ?? []}
            onBlur={formik.handleBlur("plan_id")}
            onChange={(value) => formik.setFieldValue("plan_id", value?.id ?? "")}
            menuPlacement="auto"
            isDisabled={isLocked}
          />
          {getFieldError("plan_id") && (
            <span className="error-text">{getFieldError("plan_id")}</span>
          )}
          <span className="pro-font-xs pro-mt-1 pro-d-block">
            Only published plans can be sold to an organisation.
          </span>
        </div>

        <div className="input-wrap pro-mb-4">
          <label htmlFor="offer_type" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Offer Type
          </label>
          <Select
            id="offer_type"
            name="offer_type"
            className={`pro-input lg`}
            classNamePrefix="pro-input"
            options={OFFER_TYPES}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.id}
            value={OFFER_TYPES.filter(
              (item) => item?.id === formik.values.offer_type
            )}
            onChange={(value) =>
              formik.setFieldValue("offer_type", value?.id ?? "prepaid")
            }
            menuPlacement="auto"
            isDisabled={isLocked}
          />
        </div>

        <div className="input-wrap pro-mb-4">
          <label htmlFor="code_type" className="pro-font-sm pro-mb-1 pro-fw-medium">
            Code Type
          </label>
          <Select
            id="code_type"
            name="code_type"
            className={`pro-input lg`}
            classNamePrefix="pro-input"
            options={CODE_TYPES}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.id}
            value={CODE_TYPES.filter(
              (item) => item?.id === formik.values.code_type
            )}
            onChange={(value) =>
              formik.setFieldValue("code_type", value?.id ?? "shared")
            }
            menuPlacement="auto"
            isDisabled={isLocked}
          />
          {formik.values.code_type === "unique" && !isEdit && (
            <span className="pro-font-xs pro-mt-1 pro-d-block">
              One single-use code is generated per seat, exportable as a CSV once
              the offer is saved.
            </span>
          )}
        </div>

        <Input
          label={"Offer Code"}
          type="text"
          id="offer_code"
          name="offer_code"
          className={`pro-input lg ${getFieldError("offer_code") && " error"}`}
          {...formik.getFieldProps("offer_code")}
          error={getFieldError("offer_code")}
          errorMessage={getFieldError("offer_code")}
          disabled={isLocked}
        />

        <Input
          label={
            isLocked
              ? `Seats (at least ${usedSeats} already used)`
              : "Seats"
          }
          type="number"
          id="seats"
          name="seats"
          className={`pro-input lg ${getFieldError("seats") && " error"}`}
          {...formik.getFieldProps("seats")}
          error={getFieldError("seats")}
          errorMessage={getFieldError("seats")}
        />

        <Input
          label={"Discount (%)"}
          type="number"
          id="discount_percent"
          name="discount_percent"
          className={`pro-input lg ${
            getFieldError("discount_percent") && " error"
          }`}
          {...formik.getFieldProps("discount_percent")}
          error={getFieldError("discount_percent")}
          errorMessage={getFieldError("discount_percent")}
          disabled={isLocked}
        />

        {/* The pricing panel: seat value and the liability figure finance signs off. */}
        <div className="pro-p-4 pro-mb-4 pro-bg-light rounded-3">
          <div className="pro-d-flex pro-justify-between pro-items-center pro-mb-3">
            <h6 className="pro-ttl pro-mb-0">Pricing</h6>
            {isPricingLoading && <span className="pro-font-sm">Pricing…</span>}
          </div>
          {pricing?.error ? (
            <p className="error-text pro-mb-0">{pricing?.error}</p>
          ) : Object.keys(pricing ?? {}).length === 0 ? (
            <p className="pro-mb-0 pro-font-sm">
              Choose a plan and a discount to see what a seat is worth.
            </p>
          ) : (
            <>
              <div className="pro-d-flex pro-justify-between pro-mb-1">
                <span className="pro-font-sm">Plan list value</span>
                <span className="pro-font-sm">{money(pricing?.list_value)}</span>
              </div>
              <div className="pro-d-flex pro-justify-between pro-mb-1">
                <span className="pro-font-sm">Seat value</span>
                <span className="pro-font-sm pro-fw-medium">
                  {money(pricing?.seat_value)}
                </span>
              </div>
              <div className="pro-d-flex pro-justify-between pro-mb-1">
                <span className="pro-font-sm">Employee pays</span>
                <span className="pro-font-sm">
                  {money(pricing?.employee_pays)}
                </span>
              </div>
              <div className="pro-d-flex pro-justify-between pro-mt-3 pro-pt-3 pro-border-top">
                <span className="pro-font-sm pro-fw-bolder">
                  Maximum liability
                </span>
                <span className="pro-font-sm pro-fw-bolder">
                  {money(pricing?.max_liability)}
                </span>
              </div>
              {pricing?.lines?.length > 0 && (
                <div className="pro-mt-3 pro-pt-3 pro-border-top">
                  <p className="pro-font-sm pro-fw-medium pro-mb-2">
                    Redemption price per item
                  </p>
                  {pricing.lines.map((line) => (
                    <div
                      key={line?.line_id ?? line?.name}
                      className="pro-d-flex pro-justify-between pro-mb-1"
                    >
                      <span className="pro-font-sm">
                        {line?.name} × {line?.quantity}
                      </span>
                      <span className="pro-font-sm">
                        {money(line?.list_price)} → {money(line?.discounted_price)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="code_valid_from"
            className="pro-font-sm pro-mb-1 pro-fw-medium"
          >
            Code Valid From
          </label>
          <input
            type="date"
            id="code_valid_from"
            name="code_valid_from"
            className={`pro-input lg ${
              getFieldError("code_valid_from") && " error"
            }`}
            {...formik.getFieldProps("code_valid_from")}
            disabled={isLocked}
          />
          {getFieldError("code_valid_from") && (
            <span className="error-text">{getFieldError("code_valid_from")}</span>
          )}
        </div>

        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="code_valid_to"
            className="pro-font-sm pro-mb-1 pro-fw-medium"
          >
            Code Valid To
          </label>
          <input
            type="date"
            id="code_valid_to"
            name="code_valid_to"
            className={`pro-input lg ${
              getFieldError("code_valid_to") && " error"
            }`}
            {...formik.getFieldProps("code_valid_to")}
          />
          {getFieldError("code_valid_to") && (
            <span className="error-text">{getFieldError("code_valid_to")}</span>
          )}
        </div>

        <Input
          label={"Plan Validity (days per employee, blank uses the plan's)"}
          type="number"
          id="plan_validity_days"
          name="plan_validity_days"
          className={`pro-input lg ${
            getFieldError("plan_validity_days") && " error"
          }`}
          {...formik.getFieldProps("plan_validity_days")}
          error={getFieldError("plan_validity_days")}
          errorMessage={getFieldError("plan_validity_days")}
          disabled={isLocked}
        />
        <span className="pro-font-xs pro-mb-4 pro-d-block">
          Counted from each employee's own redemption, not from the offer window.
        </span>

        <div className="input-wrap pro-mb-4">
          <label
            htmlFor="eligibility_type"
            className="pro-font-sm pro-mb-1 pro-fw-medium"
          >
            Who may redeem
          </label>
          <Select
            id="eligibility_type"
            name="eligibility_type"
            className={`pro-input lg`}
            classNamePrefix="pro-input"
            options={ELIGIBILITY_TYPES}
            getOptionLabel={(option) => option?.name}
            getOptionValue={(option) => option?.id}
            value={ELIGIBILITY_TYPES.filter(
              (item) => item?.id === formik.values.eligibility_type
            )}
            onChange={(value) =>
              formik.setFieldValue("eligibility_type", value?.id ?? "open")
            }
            menuPlacement="auto"
            isDisabled={isLocked}
          />
          <span className="pro-font-xs pro-mt-1 pro-d-block">
            Checked against the account's own mobile or email, never against
            anything the app sends.
          </span>
        </div>

        {formik.values.eligibility_type === "email_domain" && (
          <Input
            label={"Email Domain"}
            type="text"
            id="email_domain"
            name="email_domain"
            className={`pro-input lg ${
              getFieldError("email_domain") && " error"
            }`}
            {...formik.getFieldProps("email_domain")}
            error={getFieldError("email_domain")}
            errorMessage={getFieldError("email_domain")}
            disabled={isLocked}
          />
        )}

        {formik.values.eligibility_type === "mobile_list" && (
          <div className="input-wrap pro-mb-4">
            <label
              htmlFor="mobile_numbers"
              className="pro-font-sm pro-mb-1 pro-fw-medium"
            >
              Mobile Numbers
            </label>
            <textarea
              id="mobile_numbers"
              rows={4}
              className={`pro-input lg`}
              placeholder="One per line, or comma separated"
              value={formik.values.mobile_numbers?.join("\n") ?? ""}
              onChange={handleMobileTextChange}
              disabled={isLocked}
            />
            <div className="input-drag pro-mt-2">
              <input
                type="file"
                accept=".csv,text/csv"
                id="mobile_csv"
                onChange={handleMobileCsvChange}
                disabled={isLocked}
              />
              <span className="input-drag-box">
                <span className="pro-font-sm">
                  or upload the organisation's CSV
                </span>
              </span>
            </div>
            <div className="pro-d-flex pro-items-center pro-justify-between pro-mt-1">
              <span className="pro-font-xs">
                {formik.values.mobile_numbers?.length ?? 0} number
                {formik.values.mobile_numbers?.length === 1 ? "" : "s"} loaded.
              </span>
              <Button
                className={"pro-btn-link pro-p-1 pro-gap-1 pro-items-center"}
                type="button"
                onClick={handleSampleCsvDownload}
              >
                <span className="pro-font-xs">Sample CSV</span>
                <span className="material-symbols-outlined x3">download</span>
              </Button>
            </div>
            {getFieldError("mobile_numbers") && (
              <span className="error-text">
                {getFieldError("mobile_numbers")}
              </span>
            )}
          </div>
        )}

        <Input
          label={"Limit per Customer"}
          type="number"
          id="limit_per_customer"
          name="limit_per_customer"
          className={`pro-input lg ${
            getFieldError("limit_per_customer") && " error"
          }`}
          {...formik.getFieldProps("limit_per_customer")}
          error={getFieldError("limit_per_customer")}
          errorMessage={getFieldError("limit_per_customer")}
          disabled={isLocked}
        />
      </div>

      <div
        className={`col-12 pro-d-flex pro-justify-end ${
          isStickyFooter && "offcanvas-footer-sticky-btns"
        }`}
      >
        <Button
          className={"pro-btn-link lg pro-px-5"}
          onClick={() => closeModal()}
        >
          Cancel
        </Button>
        <Button
          className={`pro-btn-primary lg pro-ms-3`}
          type="submit"
          onClick={formik.handleSubmit}
          disabled={isOfferSaving}
        >
          {!isEdit
            ? isOfferSaving
              ? "Creating..."
              : "Create"
            : isOfferSaving
            ? "Updating..."
            : "Update"}
        </Button>
      </div>
    </div>
  );
};

export default AddCorporateOffer;
